import React, { useState, useEffect } from 'react';
import './TalkToSalesModal.css';
import { Button } from '../Button';

/**
 * TalkToSalesModal - A comprehensive form modal for collecting sales inquiries
 * 
 * Features:
 * - Real-time client-side validation with smart error handling
 * - Phone number auto-formatting as user types
 * - Success state with confirmation message after form submission
 * - Accessibility compliant (ARIA labels, keyboard navigation, screen reader support)
 * - Responsive design for mobile and desktop
 * - Form state management with loading states
 * 
 * Validation Rules:
 * - First/Last Name: Required, 2-50 characters, letters/spaces/hyphens/apostrophes only
 * - Email: Required, valid email format, max 254 characters
 * - Phone: Optional, but if provided: 10-15 digits (flexible formatting), auto-formatted to (XXX) XXX-XXXX
 * - ZIP Code: Optional, but if provided: 5-digit or 9-digit format (12345 or 12345-6789)
 * 
 * User Flow:
 * 1. User fills out form with validation feedback
 * 2. On successful submission, shows success state with confirmation
 * 3. User can close modal from success state
 * 4. Modal resets to form state when reopened
 */

export interface TalkToSalesFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
}

export interface TalkToSalesModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for form submission */
  onSubmit: (data: TalkToSalesFormData) => void;
  /** Optional CSS class name */
  className?: string;
}

export const TalkToSalesModal: React.FC<TalkToSalesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  className = '',
}) => {
  const [formData, setFormData] = useState<TalkToSalesFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    zipCode: '',
  });
  
  const [errors, setErrors] = useState<Partial<TalkToSalesFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        zipCode: '',
      });
      setErrors({});
      setIsSubmitting(false);
      setIsSuccess(false);
      setSubmitError('');
    }
  }, [isOpen]);

  // Handle overlay click to close
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      if (isSuccess) {
        handleCloseFromSuccess();
      } else {
        onClose();
      }
    }
  };

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        if (isSuccess) {
          handleCloseFromSuccess();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isSuccess]);

  // Validation rules
  const validateField = (field: keyof TalkToSalesFormData, value: string): string => {
    const trimmedValue = value.trim();
    
    switch (field) {
      case 'firstName':
        if (!trimmedValue) {
          return 'First name is required';
        }
        if (trimmedValue.length < 2) {
          return 'First name must be at least 2 characters';
        }
        if (trimmedValue.length > 50) {
          return 'First name must be less than 50 characters';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
          return 'First name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
        
      case 'lastName':
        if (!trimmedValue) {
          return 'Last name is required';
        }
        if (trimmedValue.length < 2) {
          return 'Last name must be at least 2 characters';
        }
        if (trimmedValue.length > 50) {
          return 'Last name must be less than 50 characters';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
          return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;
        
      case 'email':
        if (!trimmedValue) {
          return 'Email address is required';
        }
        if (trimmedValue.length > 254) {
          return 'Email address is too long';
        }
        // More comprehensive email validation
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!emailRegex.test(trimmedValue)) {
          return 'Please enter a valid email address';
        }
        break;
        
      case 'phoneNumber':
        // Phone number is optional, but if provided, validate format
        if (trimmedValue) {
          // Remove all non-digit characters for validation
          const digitsOnly = trimmedValue.replace(/\D/g, '');
          if (digitsOnly.length < 10) {
            return 'Phone number must be at least 10 digits';
          }
          if (digitsOnly.length > 15) {
            return 'Phone number must be less than 15 digits';
          }
          // More flexible phone number validation - if we have 10+ digits, it's valid
          // The formatting function already handles the display format
        }
        break;
        
      case 'zipCode':
        // ZIP code is optional, but if provided, validate format
        if (trimmedValue) {
          if (trimmedValue.length < 5) {
            return 'ZIP code must be at least 5 characters';
          }
          if (trimmedValue.length > 10) {
            return 'ZIP code must be less than 10 characters';
          }
          // Allow both 5-digit and 9-digit ZIP codes (with or without hyphen)
          if (!/^\d{5}(-\d{4})?$/.test(trimmedValue)) {
            return 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
          }
        }
        break;
    }
    
    return '';
  };

  // Handle input changes with smart validation timing
  const handleInputChange = (field: keyof TalkToSalesFormData, value: string) => {
    let processedValue = value;
    
    // Format phone number as user types
    if (field === 'phoneNumber') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }));
    
    // Only clear errors if field was previously invalid and user is now typing
    // This prevents aggressive validation while typing, but clears errors when user fixes them
    if (errors[field] && processedValue.trim()) {
      const error = validateField(field, processedValue);
      if (!error) {
        setErrors(prev => ({
          ...prev,
          [field]: '',
        }));
      }
    }
  };

  // Handle input blur for immediate validation feedback
  const handleInputBlur = (field: keyof TalkToSalesFormData) => {
    const error = validateField(field, formData[field]);
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const newErrors: Partial<TalkToSalesFormData> = {};

    // Validate all fields using the validateField function
    Object.keys(formData).forEach((key) => {
      const field = key as keyof TalkToSalesFormData;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format phone number as user types
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply formatting based on length
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else if (cleaned.length <= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else {
      // For international numbers, just add formatting for first 10 digits
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}${cleaned.slice(10)}`;
    }
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      // Focus on first field with error for better UX
      const firstErrorField = Object.keys(errors).find(key => errors[key as keyof TalkToSalesFormData]);
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        element?.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError(''); // Clear any previous errors
    
    try {
      await onSubmit(formData);
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close from success state
  const handleCloseFromSuccess = () => {
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`talk-to-sales-modal__overlay ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="talk-to-sales-modal-title"
    >
      <div className="talk-to-sales-modal">
        {isSuccess ? (
          // Success State
          <>
            <div className="talk-to-sales-modal__header">
              <h2 id="talk-to-sales-modal-title" className="talk-to-sales-modal__success-title">THE REQUEST IS SENT</h2>
              <button 
                className="talk-to-sales-modal__close-button"
                onClick={handleCloseFromSuccess}
                aria-label="Close modal"
                type="button"
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="talk-to-sales-modal__success-content">
              <p className="talk-to-sales-modal__success-message">
                Thank you for reaching out! Our team will contact you within the next 1-2 business days to assist you further.
              </p>
            </div>
          </>
        ) : (
          // Form State
          <>
            <div className="talk-to-sales-modal__header">
              <h2 id="talk-to-sales-modal-title" className="talk-to-sales-modal__title">TALK TO SALES</h2>
              <button 
                className="talk-to-sales-modal__close-button"
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                <svg 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <p className="talk-to-sales-modal__subtitle">
              Fill up the form and we'll contact you in no time
            </p>

            <form onSubmit={handleSubmit} className="talk-to-sales-modal__form" noValidate>
          <div className="talk-to-sales-modal__form-group">
            <label 
              htmlFor="firstName" 
              className="talk-to-sales-modal__label"
            >
              First Name*
            </label>
            <input
              type="text"
              id="firstName"
              className={`talk-to-sales-modal__input ${errors.firstName ? 'error' : ''}`}
              placeholder="e.g. John"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              onBlur={() => handleInputBlur('firstName')}
              disabled={isSubmitting}
              maxLength={50}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <span className="talk-to-sales-modal__error">{errors.firstName}</span>
            )}
          </div>

          <div className="talk-to-sales-modal__form-group">
            <label 
              htmlFor="lastName" 
              className="talk-to-sales-modal__label"
            >
              Last Name*
            </label>
            <input
              type="text"
              id="lastName"
              className={`talk-to-sales-modal__input ${errors.lastName ? 'error' : ''}`}
              placeholder="e.g. Wilson"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              onBlur={() => handleInputBlur('lastName')}
              disabled={isSubmitting}
              maxLength={50}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <span className="talk-to-sales-modal__error">{errors.lastName}</span>
            )}
          </div>

          <div className="talk-to-sales-modal__form-group">
            <label 
              htmlFor="email" 
              className="talk-to-sales-modal__label"
            >
              Email Address*
            </label>
            <input
              type="email"
              id="email"
              className={`talk-to-sales-modal__input ${errors.email ? 'error' : ''}`}
              placeholder="e.g. johnwilson@email.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={() => handleInputBlur('email')}
              disabled={isSubmitting}
              maxLength={254}
              autoComplete="email"
            />
            {errors.email && (
              <span className="talk-to-sales-modal__error">{errors.email}</span>
            )}
          </div>

          <div className="talk-to-sales-modal__form-group">
            <label 
              htmlFor="phoneNumber" 
              className="talk-to-sales-modal__label"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              className={`talk-to-sales-modal__input ${errors.phoneNumber ? 'error' : ''}`}
              placeholder="(000) 000-0000"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              onBlur={() => handleInputBlur('phoneNumber')}
              disabled={isSubmitting}
              maxLength={17}
              autoComplete="tel"
            />
            {errors.phoneNumber && (
              <span className="talk-to-sales-modal__error">{errors.phoneNumber}</span>
            )}
          </div>

          <div className="talk-to-sales-modal__form-group">
            <label 
              htmlFor="zipCode" 
              className="talk-to-sales-modal__label"
            >
              ZIP Code
            </label>
            <input
              type="text"
              id="zipCode"
              className={`talk-to-sales-modal__input ${errors.zipCode ? 'error' : ''}`}
              placeholder="e.g. 12345"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              onBlur={() => handleInputBlur('zipCode')}
              disabled={isSubmitting}
              maxLength={10}
              autoComplete="postal-code"
            />
            {errors.zipCode && (
              <span className="talk-to-sales-modal__error">{errors.zipCode}</span>
            )}
          </div>

          {submitError && (
            <div className="talk-to-sales-modal__submit-error">
              {submitError}
            </div>
          )}

          <div className="talk-to-sales-modal__actions">
            <Button
              type="outline"
              size="medium"
              onClick={onClose}
              disabled={isSubmitting}
            >
              CANCEL
            </Button>
            <Button
              type="primary"
              size="medium"
              onClick={() => {
                const form = document.querySelector('.talk-to-sales-modal__form') as HTMLFormElement;
                if (form) {
                  form.requestSubmit();
                }
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'SENDING...' : 'SEND REQUEST'}
            </Button>
          </div>
        </form>
        </>
      )}
      </div>
    </div>
  );
};
